import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Check, Share2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

/**
 * Growth widget shown on the user profile.
 * Lets the user share a WhatsApp invite link that carries their referral code
 * so we can attribute new signups back to them.
 */
export default function InviteWhatsAppCard({ user, testId = 'invite-whatsapp' }) {
  const [copied, setCopied] = useState(false);
  const [refCount, setRefCount] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/me/referrals');
        if (mounted) setRefCount(data?.total ?? 0);
      } catch (e) { console.warn('Failed to load referrals', e); }
    })();
    return () => { mounted = false; };
  }, []);

  // Referral link: /?ref=<username> lands on the LandingPage which sets a cookie/localStorage
  // that the signup flow picks up.
  const link = useMemo(() => {
    if (!user?.username) return '';
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    return `${base}/?ref=${encodeURIComponent(user.username)}`;
  }, [user?.username]);

  const message = useMemo(() => {
    const lines = [
      `⚡ Hey! Estoy farmeando mi AURA con @${user?.username || 'un_amigo'}`,
      `Únete al movimiento AURA — es gratis, obtienes 10,000 puntos al registrarte y podemos retarnos en batallas por puntos.`,
      ``,
      `Regístrate con mi link:`,
      link,
    ];
    return lines.join('\n');
  }, [link, user?.username]);

  const whatsappHref = useMemo(() => {
    return `https://wa.me/?text=${encodeURIComponent(message)}`;
  }, [message]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copiado');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.warn('Clipboard write failed', e);
      toast.error('No se pudo copiar');
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyLink(); return; }
    try {
      await navigator.share({
        title: 'AURA — Farmea tu Aura',
        text: message,
        url: link,
      });
    } catch (e) {
      // user cancelled — not an error
      if (e?.name !== 'AbortError') console.warn('Native share failed', e);
    }
  };

  if (!user?.username) return null;

  return (
    <section
      data-testid={testId}
      className="aura-card p-5 relative overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#25D366]/25 blur-3xl" />
      <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-aura-cyan/15 blur-3xl" />

      <div className="relative">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0 border border-[#25D366]/40 bg-[#25D366]/10">
            <Users size={20} className="text-[#25D366]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-[#25D366]">
              Growth · WhatsApp
            </div>
            <h3 className="font-display font-black text-white text-lg leading-tight">
              HAZ CRECER TU COMUNIDAD
            </h3>
            <p className="text-xs text-aura-textDim mt-1">
              Comparte por WhatsApp el link para que tus amigos se registren en AURA.
            </p>
          </div>
        </div>

        {/* Link preview / copy */}
        <div className="flex items-center gap-2 bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 mb-3">
          <span className="text-[11px] font-mono text-aura-cyan truncate flex-1" data-testid="invite-link">{link}</span>
          <button
            type="button"
            onClick={copyLink}
            data-testid="btn-copy-link"
            className="shrink-0 rounded-lg px-2 py-1 border border-white/10 hover:border-aura-cyan/50 transition text-[11px] font-display font-bold text-white flex items-center gap-1"
          >
            {copied ? <Check size={12} className="text-aura-cyan" /> : <Copy size={12} />}
            {copied ? 'OK' : 'Copiar'}
          </button>
        </div>

        {/* CTA buttons */}
        <div className="grid grid-cols-2 gap-2">
          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="btn-share-whatsapp"
            className="flex items-center justify-center gap-2 py-3 rounded-xl font-display font-black tracking-widest text-sm text-white bg-[#25D366] hover:brightness-110 transition"
          >
            <WhatsAppIcon size={18} /> ENVIAR POR WHATSAPP
          </a>
          <button
            onClick={nativeShare}
            data-testid="btn-native-share"
            className="aura-btn-ghost justify-center !py-3"
          >
            <Share2 size={16} /> Más opciones
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between text-[10px] text-aura-textDim">
          <span>Cada amigo que se registre con tu link suma a tu comunidad AURA.</span>
          {refCount != null && (
            <span data-testid="invite-count" className="font-mono font-black text-aura-cyan text-sm">
              {refCount.toLocaleString()} <span className="text-aura-textDim font-display">invitados</span>
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function WhatsAppIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.884 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}
